import React, {memo} from 'react';
import Image from 'next/image';
import parse from 'html-react-parser';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import {borderRadius, colors, contentMaxWidth, fontSize, fontWeight, lineHeight, overlay, transition, typography} from '@/styles/tokens';

export interface HoverRevealCard {
    title: string;
    /** May carry inline links and emphasis, so it is parsed as HTML. */
    description: string;
    /** Portrait artwork, 3:4. */
    image: string;
}

export interface HoverRevealCardsProps {
    heading: string;
    /** May carry inline links, so it is parsed as HTML. */
    subheading?: string;
    cards: HoverRevealCard[];
}

const DESC_CLASS = 'hover-reveal-desc';
const DEEP_SHADE_CLASS = 'hover-reveal-deep-shade';
const IMAGE_CLASS = 'hover-reveal-image';

// Expanded state: the description opens, which pushes the title up the card.
const revealedSx = {
    [`& .${DESC_CLASS}`]: {gridTemplateRows: '1fr', opacity: 1, mt: 1},
    [`& .${DEEP_SHADE_CLASS}`]: {opacity: 1}
} as const;

const gridSx = {
    listStyle: 'none',
    m: 0,
    p: 0,
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))'},
    gap: {xs: 2, md: 3}
} as const;

const cardSx = {
    position: 'relative',
    aspectRatio: '3 / 4',
    overflow: 'hidden',
    borderRadius: borderRadius.xl,
    bgcolor: colors.primary.deep,
    '&:hover, &:focus-within': revealedSx,
    [`&:hover .${IMAGE_CLASS}`]: {transform: 'scale(1.05)'},
    // Touch screens have no hover, so the description is always open there.
    '@media (hover: none)': revealedSx,
    '@media (prefers-reduced-motion: reduce)': {
        [`& .${DESC_CLASS}`]: {transition: 'none'},
        [`&:hover .${IMAGE_CLASS}`]: {transform: 'none'}
    }
} as const;

const imageStyle: React.CSSProperties = {objectFit: 'cover', transition: `transform ${transition.slow}`};

// Darkens the lower part of the artwork so white copy stays legible over any image.
const shadeSx = {
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(180deg, transparent 40%, ${overlay.dark80} 100%)`,
    pointerEvents: 'none'
} as const;

// Faded in with the description: the copy then reaches higher up the artwork, over
// brighter areas the base shade doesn't cover. Starts dark well above the copy so the
// title and description sit on a solid shade.
const deepShadeSx = {
    ...shadeSx,
    background: `linear-gradient(180deg, transparent 0%, ${overlay.dark55} 30%, ${overlay.dark80} 55%)`,
    opacity: 0,
    transition: `opacity ${transition.slow}`
} as const;

const contentSx = {position: 'absolute', left: 0, right: 0, bottom: 0, p: {xs: 2.5, md: 3}, color: colors.text.inverse} as const;

const titleSx = {fontSize: fontSize[20], fontWeight: fontWeight.bold, lineHeight: lineHeight[28], color: 'inherit'} as const;

// Collapsed to zero height with a 0fr row; opening to 1fr animates to the copy's own
// height without measuring it.
const descWrapSx = {
    display: 'grid',
    gridTemplateRows: '0fr',
    opacity: 0,
    mt: 0,
    transition: `grid-template-rows ${transition.slow}, opacity ${transition.slow}, margin-top ${transition.slow}`
} as const;

const descSx = {
    ...typography.body,
    minHeight: 0,
    overflow: 'hidden',
    color: colors.text.inverse,
    '& a': {color: 'inherit'}
} as const;

// The heading spans the full container so long headings wrap less on desktop; the
// subheading keeps the standard measure. The responsive cap needs a responsive override.
const headerWrapSx = {maxWidth: {xs: 'none', md: 'none'}, mb: {xs: 2.5, md: 3}} as const;
const headerSubheadingSx = {maxWidth: contentMaxWidth.standard, mx: 'auto'} as const;

const HoverRevealCards = ({heading, subheading, cards}: HoverRevealCardsProps) => (
    <MUIContainer>
        <SectionHeader
            heading={heading}
            subheading={subheading ? parse(subheading) : undefined}
            wrapperSx={headerWrapSx}
            subheadingSx={headerSubheadingSx}
        />

        <Box component="ul" sx={gridSx}>
            {cards.map((card) => (
                <Box component="li" key={card.title} sx={cardSx}>
                    <Image
                        src={card.image}
                        alt={card.title}
                        fill
                        sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 25vw"
                        className={IMAGE_CLASS}
                        style={imageStyle}
                    />
                    <Box sx={shadeSx} aria-hidden="true" />
                    <Box className={DEEP_SHADE_CLASS} sx={deepShadeSx} aria-hidden="true" />
                    <Box sx={contentSx}>
                        <Typography component="h3" className="font-headers" sx={titleSx}>
                            {card.title}
                        </Typography>
                        <Box className={DESC_CLASS} sx={descWrapSx}>
                            <Typography component="p" className="font-regular-text" sx={descSx}>
                                {parse(card.description)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ))}
        </Box>
    </MUIContainer>
);

export default memo(HoverRevealCards);
